/* eslint eqeqeq: [2, "smart"] */

/*
 * Generalized handling of basic crud operations on a REST resource for the redux framework.
 * See the documentation of redux under http://redux.js.org/ for a detailed explanation of the appropriate concepts.
 */

/**
 * Creates common redux action types for basic crud operations on a REST resource
 *
 * @param {String} resourceName - a unique name to be used for the appropriate REST resource
 * @returns {Object} an object with the standard action types for start, success and error actions
 *         These action types are meant to be used by appropriate redux reducers to be able to act
 *         on state changes in a distinct manner
 */
export function actionTypesFor(resourceName) {
    return {
        fetchStart: prefix('FETCH') + 'START',
        fetchSuccess: prefix('FETCH') + 'SUCCESS',
        fetchError: prefix('FETCH') + 'ERROR',
        createStart: prefix('CREATE') + 'START',
        createSuccess: prefix('CREATE') + 'SUCCESS',
        createError: prefix('CREATE') + 'ERROR',
        updateStart: prefix('UPDATE') + 'START',
        updateSuccess: prefix('UPDATE') + 'SUCCESS',
        updateError: prefix('UPDATE') + 'ERROR',
        deleteStart: prefix('DELETE') + 'START',
        deleteSuccess: prefix('DELETE') + 'SUCCESS',
        deleteError: prefix('DELETE') + 'ERROR',
        patchStart: prefix('PATCH') + 'START',
        patchSuccess: prefix('PATCH') + 'SUCCESS',
        patchError: prefix('PATCH') + 'ERROR',
        patchMergePatchJsonSuccess: prefix('PATCH_MERGE_PATCH_JSON') + 'SUCCESS'
    }

    /**
     * @param {string} action
     * @returns {string}
     */
    function prefix(action) {
        return resourceName.toUpperCase() + '_' + action + '_'
    }
}

/**
 * @typedef {Object} ReduxAction
 *
 * @property {boolean}  resetState        - True, if the state (of the Redux store property) will be resetted before
 *                                          new data is added to the property.
 * @property {string}   resourceName      - An unique name to be used for the appropriate REST resource, e.g. 'drivers'.
 * @property {string}   type              - A standard action type, e.g. 'DRIVERS_FETCH_START'.
 */

/**
 * Creates appropriate redux action creators with its associated data, that can be dispatched via redux
 *
 * @param {string} resourceName - an unique name to be used for the appropriate REST resource
 * @returns {ReduxAction} an object containing redux action creators for each crud action
 */
export function actionCreatorsFor(resourceName) {
    const actionTypes = actionTypesFor(resourceName)
    return {
        fetchStart(requestKey) {
            return { type: actionTypes.fetchStart, resourceName, requestKey }
        },
        fetchSuccess(data, requestKey, resetState = true) {
            return {
                type: actionTypes.fetchSuccess,
                data,
                resetState,
                resourceName,
                requestKey
            }
        },
        fetchError(error, requestKey) {
            return { type: actionTypes.fetchError, error, resourceName, requestKey }
        },
        createStart() {
            return { type: actionTypes.createStart, resourceName }
        },
        createSuccess(data) {
            return { type: actionTypes.createSuccess, data, resourceName }
        },
        createError(error) {
            return { type: actionTypes.createError, error, resourceName }
        },
        updateStart() {
            return { type: actionTypes.updateStart, resourceName }
        },
        updateSuccess(data) {
            return { type: actionTypes.updateSuccess, data, resourceName }
        },
        updateError(error) {
            return { type: actionTypes.updateError, error, resourceName }
        },
        deleteStart() {
            return { type: actionTypes.deleteStart, resourceName }
        },
        deleteSuccess(data) {
            return { type: actionTypes.deleteSuccess, data, resourceName }
        },
        deleteError(error) {
            return { type: actionTypes.deleteError, error, resourceName }
        },
        patchStart() {
            return { type: actionTypes.patchStart, resourceName }
        },
        patchSuccess(data) {
            return { type: actionTypes.patchSuccess, data, resourceName }
        },
        patchError(error) {
            return { type: actionTypes.patchError, error, resourceName }
        },
        patchMergePatchJsonSuccess(data) {
            return { type: actionTypes.patchMergePatchJsonSuccess, data, resourceName }
        }
    }
}

export function actionCreatorWithParentKey(resourceName, parentKey) {
    const actionCreators = actionCreatorsFor(resourceName)
    return Object.keys(actionCreators).reduce((actionCreatorsWithParentKey, actionCreatorName) => {
        actionCreatorsWithParentKey[actionCreatorName] = (...parameters) => ({
            ...actionCreators[actionCreatorName](...parameters),
            parentKey
        })
        return actionCreatorsWithParentKey
    }, {})
}
